import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../../store';
import { ordersApi } from '../../services/orders';
import { Message, OrderDetails, OrderListItem, OrderProduct, OrderStatusChangePayload, PlanListItem, ResourceListItem, VendorListItem } from '../../types';
import { orderMessagesApi } from '../../services/orderMessages';

interface OrdersState {
	orderListItems: OrderListItem[];
	orderDetails: OrderDetails | null;
	orderMaterials: ResourceListItem[];
	orderProducts: OrderProduct[];
	orderMessages: Message[];
	orderVendor: VendorListItem | null;
	orderPlan: PlanListItem | null;
}

const initialState: OrdersState = {
	orderListItems: [],
	orderDetails: null,
	orderMaterials: [],
	orderProducts: [],
	orderMessages: [],
	orderVendor: null,
	orderPlan: null,
};

export const orderSlice = createSlice({
	name: 'orders',
	initialState,
	reducers: {
		orderListItemsSetState(state: OrdersState, action) {
			state.orderListItems = action.payload;
			return state;
		},
		orderDetailsSetState(state: OrdersState, action) {
			const {messages, vendor, materials, products, plan, ...orderDetails} = action.payload;
			state.orderDetails = orderDetails;
			state.orderMaterials = materials;
			state.orderMessages = messages;
			state.orderVendor = vendor;
			state.orderPlan = plan;
			state.orderProducts = products;
			return state;
		},
		orderDetailsUpdateState(state: OrdersState, action) {
			state.orderDetails = state.orderDetails?.id === action.payload.id ? {...state.orderDetails, ...action.payload} : state.orderDetails;
		},
		orderMessagesAdd(state: OrdersState, action) {
			state.orderMessages = [...state.orderMessages, action.payload];
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(ordersApi.endpoints.orderListLoad.matchFulfilled, (state: OrdersState, action: PayloadAction<OrderListItem>) => {
				orderSlice.caseReducers.orderListItemsSetState(state,	orderSlice.actions.orderListItemsSetState(action.payload));
			})
			.addMatcher(ordersApi.endpoints.orderDetailsLoad.matchFulfilled, (state: OrdersState, action: PayloadAction<OrderDetails>) => {
				orderSlice.caseReducers.orderDetailsSetState(state,	orderSlice.actions.orderDetailsSetState(action.payload));
			})
			.addMatcher(ordersApi.endpoints.orderCancel.matchFulfilled, (state: OrdersState, action: PayloadAction<OrderStatusChangePayload>) => {
				orderSlice.caseReducers.orderDetailsUpdateState(state, orderSlice.actions.orderDetailsUpdateState(action.payload));
			})
			.addMatcher(ordersApi.endpoints.orderMockPaymentCompleted.matchFulfilled, (state: OrdersState, action: PayloadAction<OrderStatusChangePayload>) => {
				orderSlice.caseReducers.orderDetailsUpdateState(state, orderSlice.actions.orderDetailsUpdateState(action.payload));
			})
			.addMatcher(orderMessagesApi.endpoints.messageSubmit.matchFulfilled, (state: OrdersState, action: PayloadAction<Message>) => {
				orderSlice.caseReducers.orderMessagesAdd(state, orderSlice.actions.orderMessagesAdd(action.payload));
			});
	},
});

export const {
	orderDetailsSetState,
} = orderSlice.actions;

export const selectOrdersState = (state: RootState) => state.orders;

export const selectOrdersListItems = createSelector([selectOrdersState], (state) => state.orderListItems);

export const selectOrderDetails = createSelector([selectOrdersState], (state) => state.orderDetails);

export const selectOrderMaterials = createSelector([selectOrdersState], (state) => state.orderMaterials);

export const selectOrderProducts = createSelector([selectOrdersState], (state) => state.orderProducts);

export const selectOrderMessages = createSelector([selectOrdersState], (state) => state.orderMessages);

export const selectOrderVendor = createSelector([selectOrdersState], (state) => state.orderVendor);

export const selectOrderPlan = createSelector([selectOrdersState], (state) => state.orderPlan);

export default orderSlice.reducer;
